import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { usePortfolio, type PortfolioProject } from "@/hooks/usePortfolio";
import { useContentMulti } from "@/hooks/useContent";
import CmsButton from "@/components/CmsButton";
import FeaturedShowcaseCard from "./FeaturedShowcaseCard";
import PortfolioDetailDialog from "./PortfolioDetailDialog";

const FeaturedWork = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { data } = usePortfolio();
  const c = useContentMulti({
    "work.featured_title": "Our Work",
    "work.featured_subtitle": "Websites and apps we have designed and built for businesses across Southern California.",
    "work.featured_button": "See All Our Work",
    "work.featured_button_link": "/our-work",
  });
  const [open, setOpen] = useState<PortfolioProject | null>(null);
  const featured = (data?.projects ?? []).filter((p) => p.is_featured).slice(0, 6);
  const catName = (id: string | null) => data?.categories.find((x) => x.id === id)?.name;

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const updateDepth = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, (window.innerHeight / 2 - (rect.top + rect.height / 2)) / window.innerHeight));
      section.style.setProperty("--featured-scroll", progress.toFixed(3));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateDepth);
    };
    updateDepth();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [featured.length]);

  if (!featured.length) return null;

  return (
    <section ref={sectionRef} className="featured-work-section relative overflow-hidden border-y border-border py-20 sm:py-28 lg:py-32">
      <div className="featured-work-depth featured-work-depth-back absolute inset-0" aria-hidden="true" />
      <div className="featured-work-depth featured-work-depth-mid absolute inset-0" aria-hidden="true" />
      <div className="featured-work-signal absolute inset-y-0 left-0 w-full" aria-hidden="true" />
      <div className="featured-work-word featured-work-depth absolute inset-0 flex items-center justify-center overflow-hidden" aria-hidden="true">
        <span>SMARTLY</span>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6">
        <div className="featured-work-header mx-auto mb-16 max-w-3xl text-center sm:mb-20">
          <div className="mb-5 flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-primary" />
            <p className="text-xs font-bold uppercase text-primary">Selected case studies</p>
            <span className="h-px w-10 bg-primary" />
          </div>
          <h2 className="featured-work-title font-display text-5xl font-extrabold uppercase leading-none text-foreground sm:text-6xl lg:text-8xl">
            {c["work.featured_title"] === "Our Work" ? <>Our <span className="text-primary">Work.</span></> : c["work.featured_title"]}
          </h2>
          <span className="mx-auto mt-7 block h-1 w-20 bg-primary" aria-hidden="true" />
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {c["work.featured_subtitle"]}
          </p>
        </div>

        <div className="featured-work-gallery grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
          {featured.map((p, index) => (
            <FeaturedShowcaseCard
              key={p.id}
              project={p}
              categoryName={catName(p.category_id)}
              index={index}
              onOpen={() => setOpen(p)}
            />
          ))}
        </div>

        <div className="mt-20 flex flex-col items-center gap-8 sm:mt-28">
          <span className="featured-work-cta-line h-20 w-px" aria-hidden="true" />
          <CmsButton
            href={c["work.featured_button_link"]}
            variant="outline"
            size="lg"
            className="featured-work-cta group h-16 rounded-sm border-primary/50 bg-transparent px-7 font-display text-sm font-bold uppercase text-foreground sm:px-12"
          >
            {c["work.featured_button"]}
            <ArrowRight className="ml-3 h-5 w-5 text-primary transition-transform duration-300 group-hover:translate-x-1 group-hover:text-primary-foreground" />
          </CmsButton>
        </div>
      </div>
      <PortfolioDetailDialog project={open} categoryName={open ? catName(open.category_id) : undefined} onClose={() => setOpen(null)} />
    </section>
  );
};

export default FeaturedWork;
